import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Building2,
  Users,
  DollarSign,
  TrendingUp,
  Plus,
  LogOut,
  Edit,
  Trash2,
  Eye,
  Ban,
  CheckCircle,
  LogIn,
  Activity,
} from "lucide-react";
import { toast } from "sonner";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";

const BACKEND_URL = window.location.origin;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { impersonate } = useAuth();
  const [stats, setStats] = useState({
    total_restaurants: 0,
    active_restaurants: 0,
    suspended_restaurants: 0,
    new_this_month: 0,
  });
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    restaurantId: null,
    restaurantName: "",
  });

  useEffect(() => {
    const user = localStorage.getItem("admin_user");
    if (!user) {
      navigate("/admin/login");
      return;
    }
    setAdminUser(JSON.parse(user));
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, restaurantsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
          headers: getAdminAuthHeader(),
        }),
        axios.get(`${BACKEND_URL}/api/admin/restaurants`, {
          headers: getAdminAuthHeader(),
        }),
      ]);

      setStats(statsRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const handleLoginAsRestaurant = async (restaurant) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/restaurants/${restaurant.id}/impersonate`,
        {},
        { headers: getAdminAuthHeader() }
      );

      // Use AuthContext impersonate method to update state
      impersonate(response.data.access_token, response.data.user);

      // Show success and redirect
      toast.success(`Logging in as ${response.data.restaurant.name}...`);

      // Small delay then navigate
      setTimeout(() => {
        navigate("/dashboard");
      }, 500);
    } catch (error) {
      console.error("Error logging in as restaurant:", error);
      toast.error(
        error.response?.data?.detail || "Failed to login as restaurant"
      );
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-100 text-green-700 border-green-300",
      suspended: "bg-yellow-100 text-yellow-700 border-yellow-300",
      inactive: "bg-red-100 text-red-700 border-red-300",
    };
    const icons = {
      active: <CheckCircle size={14} />,
      suspended: <Ban size={14} />,
      inactive: <Ban size={14} />,
    };
    return {
      color: colors[status] || "bg-gray-100 text-gray-700 border-gray-300",
      icon: icons[status],
    };
  };

  const handleViewRestaurant = (restaurantId) => {
    navigate(`/admin/restaurants/${restaurantId}`);
  };

  const handleEditRestaurant = (restaurantId) => {
    navigate(`/admin/restaurants/${restaurantId}/edit`);
  };

  const handleDeleteRestaurant = (restaurant) => {
    setDeleteDialog({
      open: true,
      restaurantId: restaurant.id,
      restaurantName: restaurant.name,
    });
  };

  const confirmDelete = async () => {
    try {
      // Hard delete with force_delete=true to permanently remove restaurant and all data
      await axios.delete(
        `${BACKEND_URL}/api/admin/restaurants/${deleteDialog.restaurantId}?force_delete=true`,
        { headers: getAdminAuthHeader() }
      );

      toast.success("Restaurant and all related data deleted permanently");
      setDeleteDialog({ open: false, restaurantId: null, restaurantName: "" });
      fetchDashboardData(); // Refresh the list
    } catch (error) {
      console.error("Error deleting restaurant:", error);
      toast.error(
        error.response?.data?.detail || "Failed to delete restaurant"
      );
    }
  };

  const handleToggleStatus = async (restaurant) => {
    const newStatus = restaurant.status === "active" ? "suspended" : "active";
    const action = newStatus === "suspended" ? "suspend" : "activate";

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/restaurants/${restaurant.id}`,
        { status: newStatus },
        { headers: getAdminAuthHeader() }
      );

      toast.success(`Restaurant ${action}d successfully`);
      fetchDashboardData(); // Refresh the list
    } catch (error) {
      console.error(`Error ${action}ing restaurant:`, error);
      toast.error(
        error.response?.data?.detail || `Failed to ${action} restaurant`
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <SuperAdminLayout>
      <>
        {/* Page Header */}
        <div
          className="w-full grid items-center px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.64rem]"
        >
          <h1 className="text-3xl font-bold ">Dashboard</h1>
          <p className="mt-1">Overview of all restaurants and system status</p>
        </div>
        <div className="p-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Total Restaurants
                </CardTitle>
                <Building2 className="h-5 w-5 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">
                  {stats.total_restaurants}
                </div>
                <p className="text-xs text-green-600 mt-1">
                  +{stats.new_this_month} this month
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Active
                </CardTitle>
                <Users className="h-5 w-5 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {stats.active_restaurants}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Operational outlets
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Suspended
                </CardTitle>
                <DollarSign className="h-5 w-5 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">
                  {stats.suspended_restaurants}
                </div>
                <p className="text-xs text-gray-500 mt-1">Need attention</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  New This Month
                </CardTitle>
                <TrendingUp className="h-5 w-5 text-gray-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {stats.new_this_month}
                </div>
                <p className="text-xs text-gray-500 mt-1">Recently added</p>
              </CardContent>
            </Card>
          </div>

          {/* Restaurants List */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>All Restaurants</CardTitle>
                <Button
                  className="bg-red-600 hover:bg-red-700"
                  onClick={() => navigate("/admin/restaurants/create")}
                >
                  <Plus size={16} className="mr-2" />
                  Add Restaurant
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {restaurants.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-semibold mb-2">
                    No restaurants yet
                  </p>
                  <p className="text-sm">
                    Create your first restaurant to get started
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-semibold text-sm">
                          Restaurant Name
                        </th>
                        <th className="text-left p-3 font-semibold text-sm">
                          Owner Email
                        </th>
                        <th className="text-left p-3 font-semibold text-sm">
                          Subdomain
                        </th>
                        <th className="text-center p-3 font-semibold text-sm">
                          Status
                        </th>
                        <th className="text-center p-3 font-semibold text-sm">
                          Commission
                        </th>
                        <th className="text-center p-3 font-semibold text-sm">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {restaurants.map((restaurant) => {
                        const statusBadge = getStatusBadge(restaurant.status);
                        return (
                          <tr
                            key={restaurant.id}
                            className="border-b hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Building2
                                    size={20}
                                    className="text-red-600"
                                  />
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {restaurant.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    ID: {restaurant.id.slice(0, 8)}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              <p className="text-sm text-gray-700">
                                {restaurant.owner_email}
                              </p>
                              <p className="text-xs text-gray-500">
                                {restaurant.owner_phone}
                              </p>
                            </td>
                            <td className="p-3">
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {restaurant.subdomain}.waiterman.com
                              </code>
                            </td>
                            <td className="p-3 text-center">
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${statusBadge.color}`}
                              >
                                {statusBadge.icon}
                                {restaurant.status}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className="text-lg font-bold text-green-600">
                                {restaurant.commission_rate}%
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleLoginAsRestaurant(restaurant)
                                  }
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Login as Restaurant"
                                >
                                  <LogIn size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleViewRestaurant(restaurant.id)
                                  }
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleEditRestaurant(restaurant.id)
                                  }
                                  title="Edit Restaurant"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleStatus(restaurant)}
                                  className={
                                    restaurant.status === "active"
                                      ? "text-yellow-600 hover:text-yellow-700"
                                      : "text-green-600 hover:text-green-700"
                                  }
                                  title={
                                    restaurant.status === "active"
                                      ? "Suspend"
                                      : "Activate"
                                  }
                                >
                                  <Ban size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleDeleteRestaurant(restaurant)
                                  }
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete Restaurant"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Confirmation Dialog */}
          <AlertDialog
            open={deleteDialog.open}
            onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  ⚠️ Permanent Deletion Warning
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <div className="space-y-2">
                    <p>
                      This will{" "}
                      <strong className="text-red-600">
                        permanently delete
                      </strong>{" "}
                      restaurant <strong>{deleteDialog.restaurantName}</strong>{" "}
                      and ALL related data including:
                    </p>
                    <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                      <li>All menu categories and items</li>
                      <li>All tables and sections</li>
                      <li>All orders and order history</li>
                      <li>All discounts and upsale rules</li>
                      <li>All staff accounts</li>
                      <li>All reservations and settings</li>
                    </ul>
                    <p className="font-bold text-red-600 mt-3">
                      This action CANNOT be undone!
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() =>
                    setDeleteDialog({
                      open: false,
                      restaurantId: null,
                      restaurantName: "",
                    })
                  }
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Restaurant
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </>
    </SuperAdminLayout>
  );
}
